import React, { useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, Trash2, Image } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';
import { useInvoiceStore } from '../stores/invoiceStore';
import { useAuthStore } from '../stores/authStore';
import { logoApi } from '../lib/api';
import { LoginRequiredDialog } from './LoginRequiredDialog';

interface LogoUploaderProps {
  compact?: boolean;
}

const LogoUploader: React.FC<LogoUploaderProps> = ({ compact = false }) => {
  const { t } = useTranslation();
  const settings = useInvoiceStore((state) => state.settings);
  const updateSettings = useInvoiceStore((state) => state.updateSettings);
  const { isAuthenticated } = useAuthStore();
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    console.log("File selected:", file.name);
    uploadFile(file);
  };

  const uploadFile = async (file: File) => {
    try {
      setIsUploading(true);
      console.log("Uploading file:", file.name);
      
      // Upload the logo using the logoApi
      const result = await logoApi.uploadLogo(file);
      console.log("Upload result:", result);
      
      if (result.success && result.logo) {
        // Update settings with the new logo
        updateSettings({ logo: result.logo });
      }
    } catch (error) {
      console.error('Logo upload failed:', error);
      alert(t('settings.logo.uploadFailed'));
    } finally {
      setIsUploading(false);
      
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteLogo = async () => {
    if (!settings.logo?.id) return;
    
    if (!window.confirm(t('settings.logo.confirmDelete'))) {
      return;
    }
    
    try {
      setIsUploading(true);
      console.log("Deleting logo");
      
      // Delete the logo using the logoApi
      await logoApi.deleteLogo();
      
      // Update local settings by explicitly setting logo to undefined
      updateSettings({ logo: undefined });
      
    } catch (error) {
      console.error('Failed to delete logo:', error);
      alert(t('settings.logo.deleteFailed'));
    } finally {
      setIsUploading(false);
    }
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isAuthenticated || isUploading) return;
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isAuthenticated || isUploading) return;
    setIsDragging(true);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    if (!isAuthenticated || isUploading) return;
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      console.log("File dropped:", file.name);
      
      // Validate file type
      const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/svg+xml'];
      if (!validTypes.includes(file.type)) {
        alert(t('settings.logo.invalidType'));
        return;
      }
      
      // Validate file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        alert(t('settings.logo.tooLarge'));
        return;
      }
      
      uploadFile(file);
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (isUploading) return;
    
    if (!isAuthenticated) {
      setShowLoginDialog(true);
      return;
    }
    
    fileInputRef.current?.click();
  };

  if (compact) {
    return (
      <div
        className="relative w-full h-full" 
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        {settings.logo?.url ? (
          <div className="relative w-full h-full">
            <div 
              className={`w-full h-full bg-transparent rounded-lg overflow-hidden flex items-center justify-center transition-all cursor-pointer ${isDragging ? 'ring-2 ring-primary-500' : isHovering ? 'ring-1 ring-gray-300' : ''}`}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={handleClick}
            >
              <img 
                src={settings.logo.url} 
                alt="Company logo" 
                className="max-h-24 max-w-full object-contain"
              />
            </div>
            
            {/* Hover overlay */}
            <AnimatePresence>
              {isHovering && !isUploading && (
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="absolute inset-0 bg-black bg-opacity-50 rounded-lg flex items-center justify-center gap-3"
                >
                  <button
                    onClick={handleClick}
                    disabled={!isAuthenticated}
                    className="w-8 h-8 rounded-full bg-white bg-opacity-90 flex items-center justify-center text-gray-700 hover:bg-opacity-100 transition-all shadow-sm"
                    title="Change Logo"
                  >
                    <Image className="w-4 h-4" />
                  </button>
                  
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteLogo();
                    }}
                    disabled={!isAuthenticated}
                    className="w-8 h-8 rounded-full bg-white bg-opacity-90 flex items-center justify-center text-red-600 hover:bg-opacity-100 transition-all shadow-sm"
                    title="Remove Logo"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ) : (
          <div 
            className={`w-full h-20 rounded-lg border-2 border-dashed cursor-pointer transition-all 
              ${isDragging 
                ? 'border-primary-500 bg-primary-50' 
                : isHovering 
                  ? 'border-primary-300 bg-primary-50/20' 
                : 'border-gray-300 bg-gray-50/40'
              } 
              flex flex-col items-center justify-center`}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={handleClick}
            data-pdf-exclude="true"
          >
            <Upload className={`h-6 w-6 ${isDragging || isHovering ? 'text-primary-500' : 'text-gray-400'}`} />
            <p className={`text-xs ${isDragging || isHovering ? 'text-primary-600' : 'text-gray-500'} mt-1`}>
              {t('settings.logo.clickOrDrop')}
            </p>
          </div>
        )}
        
        {isUploading && (
          <div className="absolute inset-0 bg-white bg-opacity-75 rounded-lg flex flex-col items-center justify-center">
            <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-xs text-gray-600 mt-1">
              {t('settings.logo.uploading')}
            </p>
          </div>
        )}
        
        <input
          type="file"
          className="sr-only"
          accept="image/jpeg,image/png,image/webp,image/svg+xml"
          onChange={handleFileChange}
          ref={fileInputRef}
          disabled={!isAuthenticated || isUploading}
        />

        <LoginRequiredDialog
          isOpen={showLoginDialog}
          onClose={() => setShowLoginDialog(false)}
          feature="logo"
        />
      </div>
    );
  }

  // Regular mode for settings
  return (
    <motion.div 
      whileHover={{ scale: 1.01 }} 
      transition={{ duration: 0.2 }}
      className="bg-white rounded-xl border border-gray-200 shadow-sm p-4"
    >
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {t('settings.logo.title')}
      </label>
      
      <div
        className="relative" 
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        {settings.logo?.url ? (
          <div className="relative">
            <div 
              className={`w-full h-32 bg-gray-50 rounded-lg overflow-hidden flex items-center justify-center p-2 transition-all cursor-pointer ${isDragging ? 'ring-2 ring-primary-500' : isHovering ? 'ring-1 ring-gray-300' : ''}`}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={handleClick}
            >
              <img 
                src={settings.logo.url} 
                alt="Company logo" 
                className="max-h-24 max-w-full object-contain"
              />
            </div>
            
            {/* Hover overlay */}
            <AnimatePresence>
              {isHovering && !isUploading && (
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="absolute inset-0 bg-black bg-opacity-50 rounded-lg flex items-center justify-center gap-4"
                >
                  <button
                    onClick={handleClick}
                    disabled={!isAuthenticated}
                    className="w-10 h-10 rounded-full bg-white bg-opacity-90 flex items-center justify-center text-gray-700 hover:bg-opacity-100 transition-all shadow-sm"
                    title="Change Logo"
                  >
                    <Image className="w-5 h-5" />
                  </button>
                  
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteLogo();
                    }}
                    disabled={!isAuthenticated}
                    className="w-10 h-10 rounded-full bg-white bg-opacity-90 flex items-center justify-center text-red-600 hover:bg-opacity-100 transition-all shadow-sm"
                    title="Remove Logo"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
            
            <div className="text-xs text-gray-500 mt-1 truncate text-center">
              {settings.logo.filename}
            </div>
          </div>
        ) : (
          <div 
            className={`w-full h-32 rounded-lg border-2 border-dashed cursor-pointer transition-all 
              ${isDragging 
                ? 'border-primary-500 bg-primary-50' 
                : isHovering 
                  ? 'border-primary-300 bg-primary-50/20' 
                : 'border-gray-300 bg-gray-50'
              } 
              flex flex-col items-center justify-center`}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={handleClick}
            data-pdf-exclude="true"
          >
            <Upload className={`h-8 w-8 mb-2 ${isDragging || isHovering ? 'text-primary-500' : 'text-gray-400'}`} />
            <p className={`text-sm ${isDragging || isHovering ? 'text-primary-600' : 'text-gray-500'}`}>
              {isDragging 
                ? t('settings.logo.dropHere')
                : t('settings.logo.placeholder')}
            </p>
            <p className="text-xs text-gray-400 mt-1">{t('settings.logo.clickOrDrop')}</p>
          </div>
        )}
        
        {isUploading && (
          <div className="absolute inset-0 bg-white bg-opacity-75 rounded-lg flex flex-col items-center justify-center">
            <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mb-2"></div>
            <p className="text-sm text-gray-600">
              {t('settings.logo.uploading')}
            </p>
            <div className="w-48 bg-gray-200 rounded-full h-1.5 mt-3">
              <div 
                className="bg-primary-600 h-1.5 rounded-full transition-all duration-300" 
                style={{ width: '100%' }}
              ></div>
            </div>
          </div>
        )}
      </div>
      
      <input
        type="file"
        className="sr-only"
        accept="image/jpeg,image/png,image/webp,image/svg+xml"
        onChange={handleFileChange}
        ref={fileInputRef}
        disabled={!isAuthenticated || isUploading}
      />
      
      {!isAuthenticated && (
        <p className="text-xs text-amber-600 mt-2 text-center">
          {t('settings.logo.loginRequired')}
        </p>
      )}
      
      <LoginRequiredDialog
        isOpen={showLoginDialog}
        onClose={() => setShowLoginDialog(false)}
        feature="logo"
      />
    </motion.div>
  );
};

export default LogoUploader; 